import { useState } from "react";

import { css } from "@compiled/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Image } from "@/components/wpl/image/src";
import { Link } from "@/components/wpl/link/src";
import { DataEventProps } from "@/libraries/wpl/foundations/src";
import { Play } from "@/libraries/wpl/foundations/src/icons/object/play";
import { YoutubeModal } from "@/patterns/modal/src";

const sectionStyles = css({
    backgroundColor: "#deebff",
    display: "flex"
});

const contentStyles = css({
    margin: "auto",
    maxWidth: "1168px",
    width: "96%",
    display: "flex",
    paddingBlockStart: "80px",
    marginBlockEnd: "0px",
    paddingBlockEnd: "0px",

    [media.below.sm]: {
        flexDirection: "column"
    },

    [media.only.sm]: {
        flexDirection: "row"
    },

    [media.above.md]: {
        paddingInlineStart: "90px",
        flexDirection: "row"
    }
});

const mainTextStyles = css({
    marginBlockEnd: "7%",
    width: "90%",

    [media.below.sm]: {
        "marginInlineStart": "10%",

        ".text-section": {
            marginInlineEnd: "12%"
        }
    },

    [media.above.sm]: {
        maxWidth: "420px",
        marginInlineEnd: "6%",
        marginBlockStart: "10%"
    },

    h2: {
        color: "#344563",

        [media.below.sm]: {
            fontSize: "1.5rem"
        },

        [media.above.sm]: {
            fontSize: "2.25rem"
        }
    }
});

const linkStyles = css({
    "display": "flex",

    [media.below.sm]: {
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center"
    },

    [media.above.sm]: {
        flexDirection: "row"
    },

    "@media (max-width: 540px)": {
        justifyContent: "start",
        alignItems: "start"
    },

    "a": {
        "display": "flex",
        "alignItems": "center",
        "color": "#ffffff",
        "marginBlockEnd": token("space.150"),

        "span": {
            fontSize: token("space.200"),
            color: "#0052cc"
        },

        "svg": {
            fill: "#0052cc",
            marginInlineEnd: token("space.100")
        },

        ":hover": {
            span: {
                color: "#0065ff"
            },

            svg: {
                fill: "#0065ff"
            }
        }
    }
});

const appLinkStyles = css({
    "display": "flex",
    "marginBlockStart": token("space.600"),
    "alignItems": "center",

    "@media (max-width: 365px)": {
        flexDirection: "column",
        alignItems: "start"
    },

    "a": {
        marginInlineEnd: token("space.150")
    }
});

const imageStyles = css({
    maxWidth: "568px",

    [media.above.sm]: {
        width: "52%",
        height: "52%",
        alignSelf: "flex-end"
    }
});

const MobileSection = () => {
    const intl = useIntl();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const sectionContent = {
        header: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.header",
            defaultMessage: "Of course we're on mobile"
        }),
        description: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.description",
            defaultMessage:
                "Stay in sync with your team and move work forward – straight from any device."
        }),
        appStoreImageAltText: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.appStoreImageAltText",
            defaultMessage: "Download on the Apple App Store"
        }),
        appStoreText: "Download on the Apple App Store",
        googlePlayImageAltText: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.googlePlayImageAltText",
            defaultMessage: "Get it on Google Play"
        }),
        googlePlayText: "Get it on Google Play",
        mobileImageAltText: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.mobileImageAltText",
            defaultMessage:
                "Confluence mobile app notification screen showing updates and comments that a team has left on multiple pages"
        }),
        learnMore: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.learnMore",
            defaultMessage: "Learn more"
        }),
        videoText: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.mobileSection.videoText",
            defaultMessage: "Watch the video"
        })
    };

    const appStoreImageLink =
        "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:8d670f96-0469-4763-9a3a-92d604d0db76/Download_on_the_App_Store_Badge_US-UK.svg?cdnVersion=1271";
    const googlePlayImageLink =
        "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:ca831509-8569-45d7-921f-76f0984ced2e/google-play-280x86.png?cdnVersion=1271";
    const appStoreLink =
        "https://itunes.apple.com/app/apple-store/id1006971684?pt=696495&ct=wac_1&mt=8";
    const googlePlayLink =
        "https://play.google.com/store/apps/details?id=com.atlassian.android.confluence.core&hl=en";
    const mobileImageLink =
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_Mobile.webp";
    const videoLink =
        "https://www.youtube.com/embed/hBWEFTs_bgQ?autoplay=1&rel=0&origin=https%3A%2F%2Fwww.atlassian.com&widget_referrer=https%3A%2F%2Fwww.atlassian.com&enablejsapi=1&widgetid=1";

    const dataEventPropsMap: { [key: string]: DataEventProps } = {
        learnMore: {
            "data-product-key": "confluence",
            "data-event-component": "linkButton",
            "data-event-container": "mobileSection",
            "data-event": "clicked",
            "data-label": sectionContent.learnMore
        },
        videoLink: {
            "data-product-key": "confluence",
            "data-event-component": "linkButton",
            "data-event-container": "videoLink",
            "data-event": "clicked",
            "data-label": sectionContent.videoText,
            "data-label-english": "Watch the video"
        },
        appStoreLink: {
            "data-product-key": "confluence",
            "data-event-component": "linkButton",
            "data-event-container": "mobileSection",
            "data-event": "clicked",
            "data-label": sectionContent.appStoreImageAltText,
            "data-label-english": sectionContent.appStoreText
        },
        googlePlayLink: {
            "data-product-key": "confluence",
            "data-event-component": "linkButton",
            "data-event-container": "mobileSection",
            "data-event": "clicked",
            "data-label": sectionContent.googlePlayImageAltText,
            "data-label-english": sectionContent.googlePlayText
        }
    };

    return (
        <section css={sectionStyles}>
            <div css={contentStyles}>
                <div css={mainTextStyles}>
                    <ContentBlock
                        headerType="h2"
                        headerText={sectionContent.header}
                        bodyText={sectionContent.description}
                    />
                    <div css={linkStyles}>
                        <div
                            css={css({
                                [media.above.sm]: {
                                    marginInlineEnd: token("space.300")
                                }
                            })}
                        >
                            <Link
                                text={sectionContent.learnMore}
                                customLinkColor="b400"
                                doesArrowAnimate={true}
                                type="basic-underline"
                                href={useLocalizedHref(
                                    "/software/confluence/mobile-app"
                                )}
                                dataEventProps={dataEventPropsMap["learnMore"]}
                            />
                        </div>
                        <a
                            href={videoLink}
                            {...dataEventPropsMap["videoLink"]}
                            onClick={(event) => {
                                event.preventDefault();
                                setIsModalOpen(true);
                            }}
                        >
                            <Play size="22" />
                            <span>{sectionContent.videoText}</span>
                        </a>
                    </div>
                    <div css={appLinkStyles}>
                        <a
                            href={appStoreLink}
                            {...dataEventPropsMap["appStoreLink"]}
                        >
                            <Image
                                src={appStoreImageLink}
                                alt={sectionContent.appStoreImageAltText}
                                aspectRatio="none"
                                css={css({
                                    width: "130px",
                                    height: "43px"
                                })}
                            />
                        </a>
                        <a
                            href={googlePlayLink}
                            {...dataEventPropsMap["googlePlayLink"]}
                        >
                            <Image
                                src={googlePlayImageLink}
                                alt={sectionContent.googlePlayImageAltText}
                                aspectRatio="none"
                                css={css({
                                    width: "130px",
                                    height: "40px"
                                })}
                            />
                        </a>
                    </div>
                </div>
                <Image
                    src={mobileImageLink}
                    alt={sectionContent.mobileImageAltText}
                    aspectRatio="none"
                    css={imageStyles}
                />
            </div>
            <YoutubeModal
                isOpen={isModalOpen}
                src={videoLink}
                title={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.mobileSection.ModalText",
                    defaultMessage: "Confluence Mobile App | Atlassian"
                })}
                closeIconLabel={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.mobileSection.closeText",
                    defaultMessage: "Close modal"
                })}
                closeModal={() => setIsModalOpen(false)}
            />
        </section>
    );
};

export default MobileSection;
